import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    debounce,
    Divider,
    Grid2 as Grid,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { useUsers } from '../../../hooks/useUsers';
import { SeeButton, EditButton } from '../../../component/buttons';

const formatLastAccess = (value) => {
    const date = dayjs(value);
    return value && date.isValid() ? date.format('DD/MM/YYYY, HH:mm:ss') : '—';
};

const RendEditCell = (params) => {
    const userRow = params.row;
    const navigate = useNavigate();
    const editText = t('users.edit');
    const seeText = t('users.see');

    return (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            <SeeButton
                onClick={() =>
                    navigate(`/users/user/${userRow.role_uuid}`, {
                        state: { user: userRow }
                    })
                }
                size="small"
            >
                <Typography variant="subtitle2">{seeText}</Typography>
            </SeeButton>

            <EditButton
                onClick={() => navigate('/users/user/edit', { state: { user: userRow } })}
                size="small"
            >
                <Typography variant="subtitle2">{editText}</Typography>
            </EditButton>
        </Grid>
    );
};

function UsersTable({ types, typeName }) {
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const typeKey = types.join(',');

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
            }, 400),
        []
    );

    useEffect(() => () => debouncedSearch.clear(), [debouncedSearch]);

    const {
        data,
        isLoading,
        rowsPerPageOptions,
        count,
        paginationModel,
        setPaginationModel
    } = useUsers(types, search);

    useEffect(() => {
        setPaginationModel((prev) => ({
            ...prev,
            page: 0
        }));
    }, [search, setPaginationModel, typeKey]);

    const tableConfig = (typeName, types) => {
        const columns = [
            {
                field: 'name',
                headerName: typeName,
                flex: 1,
                minWidth: 220
            },
            {
                field: 'last_access',
                headerName: t('users.lastAccess'),
                flex: 1,
                minWidth: 240,
                renderCell: (params) => formatLastAccess(params.row.last_access)
            }
        ];

        if (!types.includes('DOCTOR')) {
            columns.push({
                field: 'edit',
                headerName: t('tableHeaders.actions'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                width: 250,
                renderCell: RendEditCell
            });
        }

        return { columns };
    };

    return (
        <Box>
            <Box sx={{ p: 2 }}>
                <TextField
                    size="small"
                    value={searchInput}
                    placeholder={t('users.searchUser')}
                    onChange={(event) => {
                        setSearchInput(event.target.value);
                        debouncedSearch(event.target.value);
                    }}
                    sx={{ width: { xs: '100%', sm: 330 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <Divider />

            <DataGrid
                sx={{
                    width: '100%',
                    border: 0,
                    borderRadius: 0,
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#FAFBFC' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                    '& .MuiDataGrid-cell': { borderColor: '#EEF1F3' },
                    '& .MuiDataGrid-columnHeader[data-field="name"], & .MuiDataGrid-cell[data-field="name"]':
                        {
                            pl: 3
                        }
                }}
                density="standard"
                loading={isLoading}
                rows={data}
                getRowId={(row) => row.user_uuid}
                columns={tableConfig(typeName, types).columns}
                paginationMode="server"
                pageSizeOptions={rowsPerPageOptions}
                rowCount={count}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
            />
        </Box>
    );
}

UsersTable.propTypes = {
    typeName: PropTypes.string,
    types: PropTypes.array
};

export default UsersTable;
